/**
 * Contrato do Painel dos Fundadores.
 *
 * Fica em `shared/` porque cliente e servidor precisam da MESMA definição, e
 * num arquivo próprio — não dentro de `types.ts` — porque o painel é um módulo
 * à parte: um dia pode virar painel web sem arrastar o resto do app junto.
 *
 * Nada aqui concede permissão. Quem decide quem é fundador é o servidor, em
 * `permissions.ts`, e ele reconfere em TODA ação. O que existe deste lado da
 * linha é só o formato dos dados.
 */

/* ---------------------------------------------------------------------- */
/* Visão geral                                                             */
/* ---------------------------------------------------------------------- */

export type AdminOverview = {
  usuarios: number;
  online: number;
  servidores: number;
  mensagens: number;
  /** Contas criadas nos últimos 7 dias. */
  novosUsuarios: number;
  banimentos: number;
  canais: number;
  salasDeVoz: number;
  /** Quando esta fotografia foi tirada. */
  em: number;
};

/* ---------------------------------------------------------------------- */
/* Usuários                                                                */
/* ---------------------------------------------------------------------- */

export type AdminUser = {
  id: string;
  nome: string;
  handle: string;
  avatarUrl: string | null;
  status: string;
  criadoEm: number;
  /** Quantos servidores esta conta integra. */
  servidores: number;
  sessoes: number;
  banido: boolean;
  motivoDoBanimento: string | null;
  /** `null` num banimento permanente; data de volta numa suspensão. */
  banidoAte: number | null;
  /** Fundador da plataforma — manda em tudo, não pode ser banido por terceiro. */
  fundador: boolean;
  /** `null` quando a conta não tem e-mail (cadastro antigo ou via handle). */
  email: string | null;
};

export type AdminUserDetail = {
  usuario: AdminUser;
  servidores: { id: string; nome: string; dono: boolean; cargos: string[] }[];
  sessoes: { id: string; criadaEm: number; expiraEm: number; agente: string | null; ip: string | null }[];
  notas: { id: string; texto: string; autor: string; criadoEm: number }[];
  /** Ações de moderação em que esta conta foi ALVO. */
  moderacao: AdminAuditEntry[];
};

/* ---------------------------------------------------------------------- */
/* Servidores                                                              */
/* ---------------------------------------------------------------------- */

export type AdminServer = {
  id: string;
  nome: string;
  icone: string | null;
  iconeUrl: string | null;
  donoId: string;
  donoHandle: string;
  membros: number;
  canais: number;
  salasDeVoz: number;
  cargos: number;
  mensagens: number;
  criadoEm: number;
};

export type AdminServerDetail = {
  servidor: AdminServer;
  membros: { id: string; handle: string; nome: string; cargos: string[] }[];
  canais: { id: string; nome: string; mensagens: number }[];
  salasDeVoz: { id: string; nome: string }[];
  cargos: { id: string; nome: string; cor: string; posicao: number; membros: number }[];
  convites: {
    code: string;
    usos: number;
    /** `null` = sem limite de usos. */
    limite: number | null;
    criadoPor: string;
    revogado: boolean;
  }[];
};

/* ---------------------------------------------------------------------- */
/* Auditoria                                                               */
/* ---------------------------------------------------------------------- */

export type AdminAuditEntry = {
  id: string;
  acao: string;
  autorId: string;
  autorNome: string;
  alvoId: string | null;
  alvoNome: string | null;
  detalhe: string;
  em: number;
};

/* ---------------------------------------------------------------------- */
/* Controles de emergência                                                 */
/* ---------------------------------------------------------------------- */

/**
 * Cada chave é um interruptor que o servidor consulta ANTES de deixar a ação
 * acontecer. São poucas de propósito: um interruptor que existe na tela mas
 * ninguém lê no servidor é pior do que não existir, porque mente sobre estar
 * protegendo alguma coisa.
 */
export const FLAGS_EMERGENCIA = [
  {
    chave: "cadastros_bloqueados",
    nome: "Bloquear novos cadastros",
    descricao: "Ninguém consegue criar conta. Quem já tem entra normalmente.",
  },
  {
    chave: "servidores_bloqueados",
    nome: "Bloquear criação de servidores",
    descricao: "Ninguém cria servidor novo. Os existentes seguem funcionando.",
  },
  {
    chave: "convites_bloqueados",
    nome: "Desativar convites",
    descricao: "Entrar por convite para de funcionar em toda a plataforma.",
  },
  {
    chave: "manutencao",
    nome: "Modo manutenção",
    descricao: "Só fundadores conseguem entrar. Todo o resto é recusado na porta.",
  },
] as const;

export type FlagEmergencia = (typeof FLAGS_EMERGENCIA)[number]["chave"];

export type AdminFlags = Record<string, boolean>;

/* ---------------------------------------------------------------------- */
/* Banidos                                                                 */
/* ---------------------------------------------------------------------- */

export type AdminBanido = {
  userId: string;
  nome: string;
  handle: string;
  motivo: string | null;
  porHandle: string;
  em: number;
  /** `null` = para sempre. Com data, é suspensão e ela mesma expira. */
  ate: number | null;
};

/** Crescimento diário, para a visão geral não ser só um retrato parado. */
export type AdminCrescimento = {
  dia: string;
  contas: number;
  mensagens: number;
}[];

/* ---------------------------------------------------------------------- */
/* Sessões de fundador                                                     */
/* ---------------------------------------------------------------------- */

/**
 * Sessão aberta de uma conta fundadora.
 *
 * É a informação mais sensível do painel: uma sessão de fundador num
 * aparelho ou IP que ninguém reconhece é o primeiro sinal de conta tomada,
 * e sem esta lista ninguém veria.
 */
export type SessaoFundador = {
  id: string;
  userId: string;
  handle: string;
  criadaEm: number;
  expiraEm: number;
  agente: string | null;
  ip: string | null;
  /** É a sessão de quem está olhando o painel agora. */
  atual: boolean;
};

/**
 * Ações que exigem a senha de quem as pede.
 *
 * Não é burocracia: uma sessão aberta e esquecida numa máquina destranca
 * tudo isto com dois cliques. A senha é a única coisa que o dono da conta
 * sabe e a cadeira vazia não.
 */
export const ACOES_COM_SENHA = [
  "admin:ban",
  "admin:deleteServer",
  "admin:setPassword",
  "admin:setFlag",
] as const;

/* ---------------------------------------------------------------------- */
/* Estado da plataforma                                                    */
/* ---------------------------------------------------------------------- */

export type EstadoServico = "operacional" | "instavel" | "degradado" | "fora";

export type AdminStatus = {
  servicos: { nome: string; estado: EstadoServico; detalhe: string }[];
  /** Segundos desde que o processo do servidor subiu. */
  uptime: number;
  versao: string;
};

/* ---------------------------------------------------------------------- */
/* Aviso de banimento                                                      */
/* ---------------------------------------------------------------------- */

export type AvisoBanimento = {
  motivo: string;
  /** Quem baniu — para a pessoa saber a quem recorrer. */
  por: string;
  em: number;
};
